import { DIALOG_WIDTH } from '@/app/_constant/dialog-width.constants';
import { BiometricLinkDialogComponent } from '@/app/Shared/biometric-link-dialog/biometric-link-dialog.component';
import { GlobalFunctionsService } from '@_services/global-functions.service';
import { MainAPiServiceService } from '@_services/main-api-service.service';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { KybSuccessDialogComponent } from '@pages/contact/contact-dialog/kyb-success-dialog/kyb-success-dialog.component';
import { SelectCompanyDialogComponent } from '@pages/contact/contact-dialog/select-company-dialog/select-company-dialog.component';
import { SubmitSmrDialogComponent } from '@pages/legal-details/submit-smr-dialog/submit-smr-dialog.component';
import { ToastrService } from 'ngx-toastr';
import {
  distinctUntilChanged,
  filter,
  startWith,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { StartKycComponent } from 'src/app/Shared/start-kyc/start-kyc.component';

@Component({
  selector: 'app-aml-ctf',
  templateUrl: './aml-ctf.component.html',
  styleUrls: ['./aml-ctf.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AMLCTFComponent implements OnInit {
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);
  private _mainAPiServiceService = inject(MainAPiServiceService);

  @Output() AMLCTFDetail: EventEmitter<any> = new EventEmitter<any>();
  @Input() matterdetailForm: FormGroup;
  @Input() errorWarningData: any;
  @Input() isEditMatter: any;
  @Input() isKycReady: boolean;
  @Input() kycTooltipMessage: string;
  amlPanelOpened = false;
  clientMatterText!: string;
  politicallyExposedPerson = true;
  foreignEntity = false;
  companyCreated: any;
  isKycLoading: boolean;
  biometricStarted: boolean;
  biometricLink: string | null = null;
  isDownloadingKYBReport = false;
  showKYBReportButton = false;
  showBiometricLink = false;
  barristername: any;
  isInvalidContact: boolean = false;
  disburseToMatter: boolean = false;
  selectedMatterGuid: any;
  totalprice: any;
  gst: any;
  sendMethod: any;

  constructor(public globalFunService: GlobalFunctionsService) {}

  ngOnInit(): void {
    const control = this.matterdetailForm?.get('Clientmattertext');
    if (control) {
      this.clientMatterText = control.value;
      control.valueChanges.subscribe((value) => {
        this.clientMatterText = value;
      });
    }
  }

  /**
   * Emits AML/CTF checkbox change events to the parent component.
   *
   * @param {any} event - Checkbox change event data.
   * @returns {void}
   */
  onCheckboxChange(event: any) {
    this.AMLCTFDetail.emit(event);
  }

  /**
   * Opens the AML panel and loads the latest KYC/KYB status
   * for the selected contact.
   *
   * @returns {void}
   */
  onPanelOpen(): void {
    this.amlPanelOpened = true;

    const kycControl = this.matterdetailForm.get('KYCSTATUS');
    const existingStatus = kycControl?.value;
    kycControl?.setValue(existingStatus ?? '', { emitEvent: false });

    this.isKycLoading = false;
  }

  /**
   * Determines whether the Start KYC button should be displayed.
   *
   * Validates required fields, email presence, KYC status,
   * and checks for invalid characters.
   *
   * @returns {boolean} True if KYC can be started; otherwise false.
   */
  canShowStartKyc(): boolean {
    const kycStatus = this.matterdetailForm.get('KYCSTATUS')?.value;
    const email1 = this.matterdetailForm.get('EMAIL')?.value;
    const email2 = this.matterdetailForm.get('EMAIL2')?.value;
    const familyName = this.matterdetailForm.get('FAMILYNAME')?.value;
    const givenNames = this.matterdetailForm.get('GIVENNAMES')?.value;
    const contactName = this.matterdetailForm.get('CONTACTNAME')?.value;
    const isKycAllowed =
      !kycStatus || kycStatus === 'Incomplete' || kycStatus === '' || kycStatus === null;
    const hasEmail = !!email1 || !!email2;
    const hasInvalidChars =
      this.hasSpecialChars(givenNames) ||
      this.hasSpecialChars(contactName) ||
      this.hasSpecialChars(familyName);
    const hasRequiredFields = hasEmail && !!familyName && !!givenNames;
    return isKycAllowed && hasRequiredFields && !hasInvalidChars;
  }

  /**
   * Downloads the KYB report PDF for the current contact.
   *
   * Converts the Base64 response into a downloadable PDF file.
   *
   * @returns {void}
   */
  downloadKYBReport(): void {
    this.isDownloadingKYBReport = true;

    const payload = {
      Action: 'getkybreport',
      Data: {
        CONTACTGUID: this.matterdetailForm.get('CONTACTGUID')?.value,
      },
    };

    this._mainAPiServiceService.getSetData(payload, 'identity-check').subscribe({
      next: (res) => {
        if (res?.CODE !== 200 || res?.STATUS !== 'success' || !res?.DATA?.REPORTBASE64) {
          return;
        }

        const base64 = res.DATA.REPORTBASE64;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `KYB_Report_${res.DATA.BIOMETRICCHECKGUID}.pdf`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastr.error('Unable to download KYB report.');
      },
      complete: () => {
        this.isDownloadingKYBReport = false;
      },
    });
  }

  /**
   * Returns a validation message indicating missing or invalid
   * information required to start KYC.
   *
   * @returns {string | null} Validation message or null if no issues exist.
   */
  getKycMissingMessage(): string | null {
    const kycStatus = this.matterdetailForm.get('KYCSTATUS')?.value;
    if (kycStatus && kycStatus !== 'Incomplete') {
      return null;
    }
    const missingFields: string[] = [];

    const email1 = this.matterdetailForm.get('EMAIL')?.value;
    const email2 = this.matterdetailForm.get('EMAIL2')?.value;
    const familyName = this.matterdetailForm.get('FAMILYNAME')?.value;
    const givenNames = this.matterdetailForm.get('GIVENNAMES')?.value;
    const contactName = this.matterdetailForm.get('CONTACTNAME')?.value;

    const hasEmail = !!email1 || !!email2;
    if (!hasEmail) {
      missingFields.push('Email or Email 2');
    }

    if (!familyName) {
      missingFields.push('Family Name');
    }

    if (!givenNames) {
      missingFields.push('First Name');
    }
    if (this.hasSpecialChars(givenNames)) {
      return 'First Names must not contain special characters.';
    }

    if (this.hasSpecialChars(contactName)) {
      return 'Contact Name must not contain special characters.';
    }
    if (this.hasSpecialChars(familyName)) {
      return 'Family Name must not contain special characters.';
    }

    return missingFields.length ? `Please provide ${missingFields.join(', ')} to start KYC.` : null;
  }

  /**
   * Opens the company selection dialog for KYB verification.
   * If a company is selected, company details are populated
   * and the KYC dialog is opened.
   *
   * @returns {void}
   */
  openStartKybDialog(): void {
    const contactType = this.matterdetailForm.get('CONTACTTYPE')?.value;
    if (contactType === 'Company') {
      const dialogRef = this.dialog.open(SelectCompanyDialogComponent, {
        disableClose: true,
        panelClass: 'select-company-dialog',
        data: {
          CONTACTGUID: this.matterdetailForm.get('CONTACTGUID')?.value,
          CONTACTNAME: this.matterdetailForm.get('CONTACTNAME')?.value,
        },
      });
      dialogRef.afterClosed().subscribe((company) => {
        if (!company) {
          return;
        }
        this.matterdetailForm.patchValue({
          COMPANYNAME: company.companyName,
          COMPANYIDENTIFIER: company.company_identifier,
          COUNTRYCODE: company.country_code,
          COMPANYCODE: company.company_code,
        });
        this.openStartKycDialog();
      });
      return;
    }
    this.openStartKycDialog();
  }

  /**
   * Opens the Start KYC/KYB dialog and initiates the verification
   * process based on the selected contact type.
   *
   * @returns {void}
   */
  openStartKycDialog(): void {
    const contactType = this.matterdetailForm.get('CONTACTTYPE')?.value;
    const isBusiness = contactType === 'Company';
    const dialogRef = this.dialog.open(StartKycComponent, {
      disableClose: true,
      width: DIALOG_WIDTH.W500,
      panelClass: 'start-kyc-dialog',
      data: {
        CONTACTGUID: this.matterdetailForm.get('CONTACTGUID')?.value,
        CONTACTNAME: this.clientMatterText,
        KYCTYPE: isBusiness ? 'Business (KYB)' : 'Customer (KYC)',
        MOBILE: this.matterdetailForm.get('MOBILE')?.value,
        EMAIL: this.matterdetailForm.get('EMAIL')?.value,
        COMPANYNAME: this.matterdetailForm.get('COMPANYNAME')?.value,
        COMPANYIDENTIFIER: this.matterdetailForm.get('COMPANYIDENTIFIER')?.value,
        COUNTRYCODE: this.matterdetailForm.get('COUNTRYCODE')?.value,
        COMPANYCODE: this.matterdetailForm.get('COMPANYCODE')?.value,
        CONTACTYPE: this.matterdetailForm.get('CONTACTTYPE')?.value,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.disburseToMatter = result.DISBURSETOMATTER === true;
        this.sendMethod = result.sendMethod;
        this.selectedMatterGuid = result.MATTERGUID ? result.MATTERGUID : null;
        this.totalprice = result.TOTALPRICE;
        this.gst = result.GST;
        if (this.matterdetailForm.get('CONTACTTYPE')?.value !== 'Company') {
          this.systemName();
        } else if (this.matterdetailForm.get('CONTACTTYPE')?.value == 'Company') {
          this.startBusinessBiometricCheck();
        }
      }
    });
  }

  /**
   * Starts the KYB biometric verification process for a company.
   *
   * Sends company details to the identity-check service and
   * handles validation, success, and error responses.
   *
   * @returns {void}
   */
  startBusinessBiometricCheck(): void {
    this.isKycLoading = true;
    if (this.biometricStarted) {
      return;
    }

    this.biometricStarted = true;

    const data: any = {
      ContactGuid: this.matterdetailForm.get('CONTACTGUID')?.value,
      CountryCode: this.matterdetailForm.get('COUNTRYCODE')?.value,
      CompanyName: this.matterdetailForm.get('COMPANYNAME')?.value,
      CompanyIdentifier: this.matterdetailForm.get('COMPANYIDENTIFIER')?.value,
      CompanyCode: this.matterdetailForm.get('COMPANYCODE')?.value,
      PRICEEXGST: this.totalprice,
      GST: this.gst,
      DISBURSETOMATTER: this.disburseToMatter,
    };

    if (this.disburseToMatter === true && this.selectedMatterGuid) {
      data.MATTERGUID = this.selectedMatterGuid;
    }
    const payload = {
      Action: 'startkybverification',
      ValidateOnly: false,
      Data: data,
    };
    this._mainAPiServiceService.getSetData(payload, 'identity-check').subscribe((res) => {
      if (res.CODE == 200 && res.STATUS == 'success') {
        this.getData(true);
        const dialogRef = this.dialog.open(KybSuccessDialogComponent, {
          width: DIALOG_WIDTH.W500,
          disableClose: true,
          data: {
            downloadLink: res.DATA.DOWNLOADLINK,
          },
        });

        dialogRef.afterClosed().subscribe((action) => {});
      } else if (res.CODE == 450 && res.STATUS == 'error') {
        this.isKycLoading = false;
        this.checkValidation(res.DATA.VALIDATIONS);
      } else {
        this.isKycLoading = false;
        this.toastr.error(res.MESSAGE);
        if (res?.MESSAGE?.includes("Contact Doesn't exist")) {
          this.toastr.error('Active Contact first.');
        }
      }
    });
  }

  /**
   * Starts the KYC process for an individual contact.
   *
   * @returns {void}
   */
  startKYC(): void {
    if (!this.matterdetailForm) {
      return;
    }
    this.systemName();
  }

  /**
   * Checks whether a string contains special characters.
   *
   * @param {string | null | undefined} value - Value to validate.
   * @returns {boolean} True if special characters exist; otherwise false.
   */
  hasSpecialChars(value: string | null | undefined): boolean {
    if (!value) return false;
    return /[^a-zA-Z0-9\s]/.test(value);
  }

  /**
   * Retrieves organisational details required for KYC/KYB processing.
   *
   * @returns {void}
   */
  systemName(): void {
    this._mainAPiServiceService
      .getSetData({ Action: 'GetData', Filters: { GroupName: 'OrganisationalDetails' } }, 'system')
      .subscribe(
        (response) => {
          if (response.CODE == 200 && response.STATUS == 'success') {
            this.barristername = response.DATA.RECORDS.BARRISTERSNAME;
            this.isKycLoading = true;
            this.biometricStarted = false;
            this.companyCreated = false;
            this.checkCompanyStatus();
          }
        },
        (error) => {
          this.toastr.error(error);
        },
      );
  }

  /**
   * Checks whether the KYB company account exists and is approved.
   * Creates a company account if required.
   *
   * @returns {void}
   */
  checkCompanyStatus(): void {
    const payload = {
      Action: 'checkCompanyStatus',
      ValidateOnly: false,
      Data: {},
    };

    this._mainAPiServiceService.getSetData(payload, 'identity-check').subscribe({
      next: (res: any) => {
        const status = res?.DATA?.COMPANYSTATUS;
        if (status === 'approved') {
          this.getData(false);
          return;
        } else if (!this.companyCreated) {
          this.companyCreated = true;
          this.createCompany();
        } else {
          this.isKycLoading = false;
          console.warn('Company created but not approved yet');
        }
      },
      error: () => (this.isKycLoading = false),
    });
  }

  /**
   * Retrieves the latest biometric verification data for a contact.
   *
   * @param {boolean} isAfterBiometric - Indicates whether the request
   * is being made after a biometric check has started.
   * @returns {void}
   */
  getData(isAfterBiometric: boolean): void {
    const payload = {
      Action: 'GetData',
      FILTERS: {
        CONTACTGUID: this.matterdetailForm.get('CONTACTGUID')?.value,
      },
    };
    this._mainAPiServiceService.getSetData(payload, 'identity-check').subscribe((res) => {
      if (res.CODE == 200 && res.STATUS == 'success') {
        const records = res?.DATA?.BIOMETRICVERIFICATIONRECORDS || [];
        if (records.length === 0 && !isAfterBiometric) {
          this.startBiometricCheck();
          return;
        }
        if (records.length > 0) {
          const verificationStatus = records[0].VERIFICATIONSTATUS || '';
          this.matterdetailForm.get('KYCSTATUS')?.setValue(verificationStatus);
          this.showKYBReportButton = verificationStatus === 'inprogress';
        } else {
          this.matterdetailForm.get('KYCSTATUS')?.setValue('Incomplete');
        }
        this.isKycLoading = false;
      } else {
        this.isKycLoading = false;
      }
    });
  }

  /**
   * Creates a company account in the identity-check system.
   *
   * @returns {void}
   */
  createCompany(): void {
    const payload = {
      Action: 'createaccount',
      ValidateOnly: false,
      Data: {
        companyName: this.barristername,
      },
    };

    this._mainAPiServiceService.getSetData(payload, 'identity-check').subscribe({
      next: () => {
        this.isKycLoading = false;
        this.checkCompanyStatus();
      },
      error: (error) => {
        this.isKycLoading = false;

        const errorMessage = error?.error?.MESSAGE || 'An error occurred while creating company';

        this.toastr.error(errorMessage, 'Error');
      },
    });
  }

  /**
   * Starts the biometric verification process for an individual contact.
   *
   * Handles SMS, Email, and Link delivery methods.
   *
   * @returns {void}
   */
  startBiometricCheck(): void {
    this.isKycLoading = true;
    if (this.biometricStarted) {
      return;
    }

    this.biometricStarted = true;
    const data: any = {
      ContactGuid: this.matterdetailForm.get('CONTACTGUID')?.value,
      PRICEEXGST: this.totalprice,
      SENDMETHOD: this.sendMethod,
      GST: this.gst,
      Reference: this.matterdetailForm.get('CONTACTID')?.value,
      DISBURSETOMATTER: this.disburseToMatter,
    };
    if (this.disburseToMatter === true && this.selectedMatterGuid) {
      data.MATTERGUID = this.selectedMatterGuid;
    }
    const payload = {
      Action: 'startbiometriccheck',
      ValidateOnly: false,
      Data: data,
    };
    this._mainAPiServiceService.getSetData(payload, 'identity-check').subscribe((res) => {
      if (res.CODE == 200 && res.STATUS == 'success') {
        this.isKycLoading = false;
        const biometricLink = res?.DATA?.BIOMETRICSTARTLINK;
        if (this.sendMethod === 'SMS' || this.sendMethod === 'Email') {
          this.toastr.success(`KYC has been successfully sent via ${this.sendMethod}.`);
        }
        if (this.sendMethod === 'Link' && biometricLink) {
          this.biometricLink = biometricLink;
          this.showBiometricLink = true;
          this.dialog.open(BiometricLinkDialogComponent, {
            width: DIALOG_WIDTH.W500,
            panelClass: 'start-kyc-dialog',
            disableClose: true,
            data: {
              link: biometricLink,
              CONTACTTYPE: this.matterdetailForm.get('CONTACTTYPE')?.value,
            },
          });
        }
        if (biometricLink && this.sendMethod !== 'Link') {
          window.open(biometricLink, '_blank', 'noopener,noreferrer');
        }
        this.getData(true);
      } else if (res.CODE == 450 && res.STATUS == 'error') {
        this.isKycLoading = false;
        this.checkValidation(res.DATA.VALIDATIONS);
      } else {
        this.isKycLoading = false;
        this.toastr.error(res.MESSAGE);
        if (res?.MESSAGE?.includes("Contact Doesn't exist")) {
          this.toastr.error('Active Contact first.');
        }
      }
    });
  }

  /**
   * Validates identity-check response validations and displays
   * validation errors when present.
   *
   * @param {any[]} validations - Validation response collection.
   * @returns {boolean} True if all validations pass; otherwise false.
   */
  checkValidation(validations: any[]): boolean {
    const invalidFields = validations.filter((v) => v.VALUEVALID === 'No');

    if (invalidFields.length > 0) {
      const errorMessages = invalidFields
        .map((v) => v.ERRORDESCRIPTION || `${v.FIELDNAME} is invalid`)
        .join(' | ');

      this.toastr.error(errorMessages);
      this.isInvalidContact = true;
      return false;
    }
    this.isInvalidContact = false;
    return true;
  }

  // getUserName(): string {
  //   const currentUserStr = localStorage.getItem('currentUser');
  //   if (!currentUserStr) return '';
  //   const currentUser = JSON.parse(currentUserStr);
  //   return currentUser?.UserName || '';
  // }

  // getKycIcon(status: string): string {
  //   return `assets/kyc-icons/${status.toLowerCase()}.svg`;
  // }

  /**
   * Returns a CSS class name based on the KYC status.
   *
   * @param {string | null | undefined} status - KYC status value.
   * @returns {string} CSS class name.
   */
  getKycStatusClass(status: string | null | undefined): string {
    return status ? status.toLowerCase() : '';
  }

  /**
   * Returns a user-friendly text prefix for the supplied KYC status.
   *
   * @param {string} status - KYC status value.
   * @returns {string} Formatted status text.
   */
  getKycStatusText(status: string): string {
    const verificationType =
      this.matterdetailForm.get('CONTACTTYPE')?.value === 'Company' ? 'KYB' : 'KYC';
    switch (status) {
      case 'Failed':
        return `${verificationType} has`;
      case 'Exception':
        return `${verificationType} is an`;
      case 'Review':
        return `${verificationType} is under`;
      default:
        return `${verificationType} is`;
    }
  }
  /**
   * Opens the Submit SMR dialog.
   *
   * @returns {void}
   */
  submitSMR(): void {
    this.dialog.open(SubmitSmrDialogComponent, {
      width: DIALOG_WIDTH.W300,
      disableClose: true,
    });
  }

  //      /**
  //    * Copies the biometric verification link to the clipboard.
  //    *
  //    * @returns {void}
  //    */
  //     copyBiometricLink(): void {
  //         if (!this.biometricLink) return;
  //         navigator.clipboard.writeText(this.biometricLink).then(() => {
  //             this.toastr.success('Link copied to clipboard');
  //         });
  //     }

  /**
   * Returns a CSS class name representing the supplied risk level.
   *
   * @param {string | null} risk - Risk rating value.
   * @returns {string} CSS class name for styling.
   */
  getRiskClass(risk: string | null): string {
    switch (risk) {
      case 'Low':
        return 'green';
      case 'Medium-Low':
        return 'blue';
      case 'Medium':
        return 'yellow';
      case 'Medium High':
        return 'orange';
      case 'High':
        return 'red';
      default:
        return '';
    }
  }

  /**
   * Returns the icon path corresponding to the supplied KYC status.
   *
   * @param {string | null | undefined} status - KYC status value.
   * @returns {string} Icon file path.
   */
  getKycStatusIconPath(status: string | null | undefined): string {
    const normalizedStatus = status.trim().toLowerCase();

    switch (normalizedStatus) {
      case 'completed':
      case 'complete':
      case 'approved':
        return 'assets/icons/web_app/Complete.svg';

      case 'pending':
        return 'assets/icons/web_app/Pending.svg';

      case 'pre-commencement':
        return 'assets/icons/web_app/Exempt.svg';

      case 'exempt':
        return 'assets/icons/web_app/Exempt.svg';

      case 'partial':
        return 'assets/icons/web_app/Partial.svg';

      case 'delayed':
        return 'assets/icons/web_app/Delayed.svg';

      case 'exception':
        return 'assets/icons/web_app/Exception.svg';

      case 'failed':
        return 'assets/icons/web_app/Failed.svg';
      case 'created':
        return 'assets/icons/web_app/Created.svg';

      case 'incomplete':
        return 'assets/icons/web_app/Incomplete.svg';

      case 'review':
        return 'assets/icons/web_app/Review status.svg';

      default:
        return 'assets/icons/web_app/Incomplete.svg';
    }
  }
}
